import React, { useMemo, useState } from 'react'
import { MenuItem, TextField } from '@material-ui/core'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
} from '../../../ContractManager/CatalogFilterChrome'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import {
    CoverageCompletionFilter,
    CoveragePropertyStatusFilter,
    CoverageScopeFilter,
} from './coverageFilters'

const SCOPE_LABELS: Record<CoverageScopeFilter, string> = {
    required: 'Required types',
    all: 'All types',
}

const COMPLETION_LABELS: Record<CoverageCompletionFilter, string> = {
    all: 'Any coverage',
    missing: 'Has missing',
    required_missing: 'Required missing',
    complete: 'Complete',
}

const PROPERTY_LABELS: Record<CoveragePropertyStatusFilter, string> = {
    all: 'All properties',
    uploaded: 'Uploaded only',
    missing: 'Missing only',
    required_missing: 'Required missing',
}

interface CoverageWorkspaceFiltersProps {
    typeSearch: string
    onTypeSearchChange: (value: string) => void
    scope: CoverageScopeFilter
    onScopeChange: (value: CoverageScopeFilter) => void
    completion: CoverageCompletionFilter
    onCompletionChange: (value: CoverageCompletionFilter) => void
    propertyStatus: CoveragePropertyStatusFilter
    onPropertyStatusChange: (value: CoveragePropertyStatusFilter) => void
    itemNoun: string
    selectedTagId?: number | null
    onSelectedTagIdChange?: (value: number | null) => void
    toolbarExtra?: React.ReactNode
}

export const CoverageWorkspaceFilters: React.FC<CoverageWorkspaceFiltersProps> = ({
    typeSearch,
    onTypeSearchChange,
    scope,
    onScopeChange,
    completion,
    onCompletionChange,
    propertyStatus,
    onPropertyStatusChange,
    itemNoun,
    selectedTagId = null,
    onSelectedTagIdChange,
    toolbarExtra,
}) => {
    const { userTags } = useUserTags()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const clearDrawerFilters = () => {
        onScopeChange('required')
        onCompletionChange('all')
        onPropertyStatusChange('all')
        onSelectedTagIdChange?.(null)
    }

    const chips = useMemo<FilterChipItem[]>(() => {
        const next: FilterChipItem[] = []
        if (scope !== 'required') {
            next.push({
                id: 'scope',
                label: `Types: ${SCOPE_LABELS[scope]}`,
                onDelete: () => onScopeChange('required'),
            })
        }
        if (completion !== 'all') {
            next.push({
                id: 'completion',
                label: `Coverage: ${COMPLETION_LABELS[completion]}`,
                onDelete: () => onCompletionChange('all'),
            })
        }
        if (propertyStatus !== 'all') {
            next.push({
                id: 'properties',
                label: `Properties: ${PROPERTY_LABELS[propertyStatus]}`,
                onDelete: () => onPropertyStatusChange('all'),
            })
        }
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange?.(null),
        )
        if (tagChip) next.push(tagChip)
        return next
    }, [
        scope,
        completion,
        propertyStatus,
        selectedTagId,
        userTags,
        onScopeChange,
        onCompletionChange,
        onPropertyStatusChange,
        onSelectedTagIdChange,
    ])

    return (
        <>
            <CatalogFilterBar
                searchPlaceholder={`Search ${itemNoun} types...`}
                searchValue={typeSearch}
                onSearchChange={onTypeSearchChange}
                onOpenFilters={() => setDrawerOpen(true)}
                chips={chips}
                onClearAll={chips.length > 0 ? clearDrawerFilters : undefined}
                toolbarExtra={toolbarExtra}
            />
            <CatalogFilterDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={`${
                    itemNoun === 'certificate'
                        ? 'Certificate'
                        : itemNoun === 'document'
                        ? 'Document'
                        : 'Contract'
                } coverage filters`}
                activeCount={chips.length}
                onClear={clearDrawerFilters}
            >
                {onSelectedTagIdChange && (
                    <PropertyTagFilterField
                        value={selectedTagId}
                        onChange={onSelectedTagIdChange}
                    />
                )}
                <TextField
                    label="Types"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={scope}
                    onChange={(event) =>
                        onScopeChange(event.target.value as CoverageScopeFilter)
                    }
                >
                    <MenuItem value="required">Required types</MenuItem>
                    <MenuItem value="all">All types</MenuItem>
                </TextField>
                <TextField
                    label="Coverage"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={completion}
                    onChange={(event) =>
                        onCompletionChange(
                            event.target.value as CoverageCompletionFilter,
                        )
                    }
                >
                    <MenuItem value="all">Any coverage</MenuItem>
                    <MenuItem value="missing">Has missing</MenuItem>
                    <MenuItem value="required_missing">
                        Required missing
                    </MenuItem>
                    <MenuItem value="complete">Complete</MenuItem>
                </TextField>
                <TextField
                    label="Properties"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={propertyStatus}
                    onChange={(event) =>
                        onPropertyStatusChange(
                            event.target.value as CoveragePropertyStatusFilter,
                        )
                    }
                >
                    <MenuItem value="all">All properties</MenuItem>
                    <MenuItem value="missing">Missing only</MenuItem>
                    <MenuItem value="required_missing">
                        Required missing
                    </MenuItem>
                    <MenuItem value="uploaded">Uploaded only</MenuItem>
                </TextField>
            </CatalogFilterDrawer>
        </>
    )
}
